import { inject, Injectable } from '@angular/core';
import { environment } from '../../../environments/environment';
import { HttpClient, HttpContext } from '@angular/common/http';
import { FAIL_BANNER, SUCCESS_BANNER } from '../../core/main.interceptor';
import { Customer, CustomerAuth } from '../../shared/models/Customer';
import { AuthStrategy } from '../../shared/enums/AuthStrategy';
import { SignUpFormModel } from '../../domains/auth/feature-sign-up/form-schema';
import { SignInFormModel } from '../../domains/auth/feature-sign-in/form-schema';
import { Observable } from 'rxjs';

@Injectable({
	providedIn: 'root',
})
export class UserApiService {
	private http = inject(HttpClient);
	constructor() {}

	public loginUserFirebaseSocial(data: {
		firebaseUid: string;
		email: string;
		idToken: string;
		strategy: string;
		customerGroup: string;
	}) {
		return this.http.post<CustomerAuth>('/v1/customer/' + 'auth/login', data);
	}

	public createUserFirebaseSocial(data: { email: string; idToken: string; customerGroup: string }) {
		let finalObj: { idToken: string; customerGroup: string; email?: string; registerInPiggy?: boolean } = {
			idToken: data.idToken,
			customerGroup: data.customerGroup,
			registerInPiggy: true,
		};
		if (data.email) finalObj.email = data.email;

		return this.http.post<CustomerAuth>('/v1/customer/auth/register' + '/firebase', finalObj);
	}

	public patchCustomer(customer: Customer) {
		customer._id = undefined;
		return this.http.patch<Customer>('/v1/customer/', customer, {
			context: new HttpContext().set(SUCCESS_BANNER, 'account.update_user_success'),
		});
	}

	public signInAnonymous(uid = undefined) {
		return this.http.post<CustomerAuth>('/v1/customer/auth/login', {
			strategy: AuthStrategy.ANONYMOUS,
			uid: uid,
			customerGroup: environment.customerGroup,
		});
	}

	public signIn(formVal: SignInFormModel) {
		return this.http.post<CustomerAuth>('/v1/customer/auth/login', {
			strategy: AuthStrategy.CREDENTIALS,
			customerGroup: environment.customerGroup,
			...formVal,
		});
	}

	public signUp(data: SignUpFormModel) {
		return this.http.post<Customer>('/v1/customer/auth/register', {
			strategy: AuthStrategy.CREDENTIALS,
			customerGroup: environment.customerGroup,
			preferredVenues: [],
			verifyWithCode: true,
			venue: environment.masterVenue,
			...data,
			isInComoLoyaltyProgram: true,
			name: data.firstName + '_' + data.lastName,
			phone: data.phone.substring(1),
		});
	}

	public sendEmailVerification(email: string) {
		return this.http.post(
			'/v1/customer/auth/verify/resend',
			{
				email,
				customerGroup: environment.customerGroup,
			},
			{
				context: new HttpContext().set(SUCCESS_BANNER, 'auth.code_sent'),
			}
		);
	}

	public async verifyUserByCode(code: string, userId: string) {
		return this.http.post<CustomerAuth>(`v1/customer/auth/verify/email-by-code/${userId}`, {
			code,
		});
	}

	public deleteAccount() {
		return this.http.delete('/v1/customer/soft', {
			context: new HttpContext()
				.set(SUCCESS_BANNER, 'account.delete_account.success')
				.set(FAIL_BANNER, 'account.delete_account.error'),
		});
	}

	public resetPassword(token: string, password: string) {
		return this.http.post('/v1/customer/auth/password/forgot/reset', {
			token,
			password,
		});
	}

	public forgotPassword(email: string) {
		return this.http.post('/v1/customer/auth/password/forgot/request', {
			email,
			customerGroup: environment.customerGroup,
			verifyWithCode: true,
		});
	}

	public resetPasswordSendCode(code: string, email: string) {
		return this.http.post<{ passwordResetToken: string }>(`/v1/customer/auth/password/by-code/token`, {
			code,
			email,
		});
	}

	refreshToken(token: string): Observable<CustomerAuth> {
		return this.http.post<CustomerAuth>('/v1/customer/auth/token/refresh', {
			refreshToken: token,
		});
	}
}
